import { createApi } from '@reduxjs/toolkit/query/react';

import { axiosBaseQuery } from '../../../shared/services/baseQuery';

import {
  DetailsType,
  type GetAssignedEntitlementApiResponse,
  type GetViolatingEntitlementApiResponse,
  type GetViolationFunctionApiResponse,
  type GetViolationMitigatingControlApiResponse,
  type ViolationConflictApisProp,
} from './detailsTypes';

export const detailsApi = createApi({
  reducerPath: DetailsType.API_SLICE_NAME,
  baseQuery: axiosBaseQuery({}),
  endpoints: () => ({}),
});

// Extend API with additional endpoints
export const detailsApiEndpoints = detailsApi.injectEndpoints({
  endpoints: (builder) => ({
    getViolationFunction: builder.mutation<unknown, ViolationConflictApisProp>({
      query: ({ violationId, payload }) => ({
        url: `/api/sodms/sod-violations/${violationId}/functions`,
        method: 'POST',
        data: payload,
        isECMv6: true,
      }),
      transformResponse: (response: GetViolationFunctionApiResponse) => {
        if (response.status === 200) {
          return response.data;
        }

        return [];
      },
    }),
    getViolationMitigatingControl: builder.mutation<
      unknown,
      { violationId: string }
    >({
      query: ({ violationId }) => ({
        url: `/api/sodms/sod-violations/${violationId}/mitigating-controls`,
        method: 'GET',
        isECMv6: true,
      }),
      transformResponse: (
        response: GetViolationMitigatingControlApiResponse
      ) => {
        if (response.status === 200) {
          return response.data;
        }

        return [];
      },
    }),
    getAssignedEntitlements: builder.mutation<
      unknown,
      ViolationConflictApisProp
    >({
      query: ({ violationId, functionId, payload }) => ({
        url: `/api/sodms/sod-violations/${violationId}/functions/${
          functionId ?? ''
        }/assigned-entitlements`,
        method: 'POST',
        data: payload,
        isECMv6: true,
      }),
      transformResponse: (response: GetAssignedEntitlementApiResponse) => {
        if (response.status === 200) {
          return { data: response.data, afterKey: response.afterKey };
        }

        return { data: [], afterKey: null };
      },
    }),
    getViolatingEntitlements: builder.mutation<
      unknown,
      ViolationConflictApisProp
    >({
      query: ({ violationId, functionId, assignedEntitlementId, payload }) => ({
        url: `/api/sodms/sod-violations/${violationId}/functions/${
          functionId ?? ''
        }/assigned-entitlements/${
          assignedEntitlementId ?? ''
        }/violating-entitlements`,
        method: 'POST',
        data: payload,
        isECMv6: true,
      }),
      transformResponse: (response: GetViolatingEntitlementApiResponse) => {
        if (response.status === 200) {
          return { data: response.data, searchAfter: response.searchAfter };
        }

        return { data: [], searchAfter: null };
      },
    }),
  }),
});

export const {
  useGetViolationFunctionMutation,
  useGetViolationMitigatingControlMutation,
  useGetAssignedEntitlementsMutation,
  useGetViolatingEntitlementsMutation,
} = detailsApiEndpoints;
